/**
 * Barcodes on staff order pages and pick sheets, for the store's checkout scanners. A store picks the type
 * its scanners read with the BARCODE_TYPE build setting. The GS1 types carry the product's GTIN (its
 * UPC/EAN number, from the feed); Data Matrix and QR Code carry the SKU. `bcid` is the bwip-js symbology.
 */
export const BARCODE_TYPES = {
  upc: { label: "GS1 UPC", bcid: "upca", data: "gtin" },
  ean: { label: "GS1 EAN", bcid: "ean13", data: "gtin" },
  "gs1-128": { label: "GS1-128", bcid: "gs1-128", data: "gtin" },
  "itf-14": { label: "GS1 ITF/ITF-14", bcid: "itf14", data: "gtin" },
  "databar-limited": { label: "GS1 DataBar Limited", bcid: "databarlimited", data: "gtin" },
  "databar-expanded": { label: "GS1 DataBar Expanded", bcid: "databarexpanded", data: "gtin" },
  "databar-omni": { label: "GS1 DataBar Omnidirectional", bcid: "databaromni", data: "gtin" },
  "databar-stacked": { label: "GS1 DataBar Stacked", bcid: "databarstacked", data: "gtin" },
  "databar-composite": { label: "GS1 DataBar Composite", bcid: "databaromnicomposite", data: "gtin" },
  "databar-truncated": { label: "GS1 DataBar Truncated", bcid: "databartruncated", data: "gtin" },
  datamatrix: { label: "Data Matrix", bcid: "datamatrix", data: "sku" },
  qrcode: { label: "QR Code", bcid: "qrcode", data: "sku" },
} as const satisfies Record<string, { label: string; bcid: string; data: "gtin" | "sku" }>;

export type BarcodeType = keyof typeof BARCODE_TYPES;
export const BARCODE_TYPE_IDS = Object.keys(BARCODE_TYPES) as BarcodeType[];

/** Code 128 underneath, like the plain SKU barcodes before there was a choice. */
export const DEFAULT_BARCODE_TYPE: BarcodeType = "gs1-128";

/** The BARCODE_TYPE setting: unset, blank or "default" takes the default; anything not listed is an error. */
export function parseBarcodeType(value: string | undefined): { type: BarcodeType; error?: string } {
  const v = value?.trim().toLowerCase();
  if (!v || v === "default") return { type: DEFAULT_BARCODE_TYPE };
  if ((BARCODE_TYPE_IDS as string[]).includes(v)) return { type: v as BarcodeType };
  return {
    type: DEFAULT_BARCODE_TYPE,
    error: `BARCODE_TYPE must be one of ${BARCODE_TYPE_IDS.join(", ")}, not "${value}".`,
  };
}

/** A GTIN-8, -12, -13 or -14 with a correct check digit. */
export function isValidGtin(gtin: string): boolean {
  if (!/^(\d{8}|\d{12,14})$/.test(gtin)) return false;
  const digits = [...gtin].map(Number);
  const check = digits.pop()!;
  // From the right: weights 3, 1, 3, 1… on the digits before the check digit.
  const sum = digits.reverse().reduce((s, d, i) => s + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

/** AI 240 (additional product identification): up to 30 of GS1's alphanumeric characters. */
const AI_240 = /^[!"%&'()*+,\-./0-9:;<=>?A-Z_a-z]{1,30}$/;

/**
 * What a barcode of this type holds for a product: the bwip-js symbology and text, or undefined when the
 * product can't have one (no GTIN, or a GTIN the type can't carry, like an EAN-13 as a UPC).
 */
export function barcodeContent(
  type: BarcodeType,
  product: { sku: string; gtin?: string },
): { bcid: string; text: string } | undefined {
  const { bcid, data } = BARCODE_TYPES[type];
  if (data === "sku") return { bcid, text: product.sku };
  if (!product.gtin || !isValidGtin(product.gtin)) return undefined;
  const g14 = product.gtin.padStart(14, "0");
  const ai01 = `(01)${g14}`;
  switch (type) {
    case "upc":
      return g14.startsWith("00") ? { bcid, text: g14.slice(2) } : undefined;
    case "ean":
      return g14.startsWith("0") ? { bcid, text: g14.slice(1) } : undefined;
    case "itf-14":
      return { bcid, text: g14 };
    case "databar-limited":
      // Limited carries indicator digits 0 and 1 only.
      return g14 < "2" ? { bcid, text: ai01 } : undefined;
    case "databar-composite":
      // The 2D part carries the SKU (AI 240), so the store's own code travels with the GTIN.
      return AI_240.test(product.sku) ? { bcid, text: `${ai01}|(240)${product.sku}` } : undefined;
    default:
      return { bcid, text: ai01 };
  }
}

/** Data Matrix and QR Code are square; the rest are read across, so they need width. */
export const isTwoDimensional = (type: BarcodeType) => type === "datamatrix" || type === "qrcode";

/**
 * How the store draws a type with bwip-js, beside its content: 2 pixels a module on white, with the quiet zone a
 * scanner needs on each side, and the digits under a linear code.
 */
export function barcodeDrawing(type: BarcodeType) {
  if (isTwoDimensional(type)) return { scale: 2, padding: 4, backgroundcolor: "FFFFFF" };
  return {
    scale: 2,
    paddingwidth: 12,
    paddingheight: 4,
    backgroundcolor: "FFFFFF",
    includetext: true,
    // UPC and EAN lay out their own digits, between and beside the guard bars.
    ...(type !== "upc" && type !== "ean" && { textxalign: "center" as const }),
  };
}

/**
 * bwip-js sets only a viewBox, which stretches to fill its box. Its natural size as width and height lets a
 * stylesheet set a minimum instead (the staff pages: 2 inches, or an inch square).
 */
export function withSvgSize(svg: string): string {
  const [, w, h] = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg) ?? [];
  return w && h ? svg.replace("<svg ", `<svg width="${w}" height="${h}" `) : svg;
}
